/* Post-Apply outcome modal — see runApplyPlan.js for the report shape.
 * Renders results in the same 4-step order as the Apply Preview and
 * offers Retry / Audit link / Copy JSON / Close actions. */
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import {
  CheckCircle2,
  XCircle,
  GitMerge,
  SkipForward,
  Send,
  X,
  RefreshCw,
  ClipboardCopy,
  ExternalLink,
  Clock,
} from 'lucide-react';

const stepMeta = {
  'merge-staging': { icon: GitMerge, label: 'Merge (staging → staging)' },
  'merge-approved': { icon: GitMerge, label: 'Merge (staging → approved)' },
  skip: { icon: SkipForward, label: 'Delete' },
  keep: { icon: Send, label: 'Keep' },
};

const STEP_ORDER = ['merge-staging', 'merge-approved', 'skip', 'keep'];

const RowChip = ({ row, tone = 'gray' }) => {
  if (!row) return null;
  const cls =
    tone === 'target'
      ? 'bg-emerald-50 text-emerald-900 border-emerald-200'
      : tone === 'approved'
      ? 'bg-teal-50 text-teal-900 border-teal-200'
      : 'bg-gray-50 text-gray-900 border-gray-200';
  const label = row.isApproved
    ? row.text
    : row.rowNumber != null
    ? `Row ${row.rowNumber}${row.isCrossBatchMatch && row.sourceFilename ? ` · ${row.sourceFilename}` : ''}`
    : row.isCrossBatchMatch
    ? `Cross-batch match${row.sourceFilename ? ` · ${row.sourceFilename}` : ''}`
    : `${(row.id || '').slice(0, 12)}…`;
  const preview = row.isApproved ? '' : (row.text || '').slice(0, 50);
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] ${cls}`}>
      <span className="font-semibold">{label}</span>
      {preview && (
        <span className="text-gray-600 truncate max-w-[240px]" title={row.text}>
          — {preview}
          {(row.text || '').length > 50 ? '…' : ''}
        </span>
      )}
    </span>
  );
};

const ResultLine = ({ r }) => {
  const Icon = r.ok ? CheckCircle2 : XCircle;
  const iconCls = r.ok ? 'text-emerald-600' : 'text-red-600';
  return (
    <li
      className={`px-2 py-1.5 border-b border-gray-100 last:border-0 ${
        r.ok ? '' : 'bg-red-50/40'
      }`}
      data-testid={`apply-result-${r.ok ? 'ok' : 'failed'}`}
    >
      <div className="flex items-start gap-2 flex-wrap">
        <Icon className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${iconCls}`} />
        <div className="flex-1 min-w-0 space-y-0.5">
          <div className="flex items-center flex-wrap gap-1">
            {r.step === 'merge-staging' && (
              <>
                <RowChip row={r.source} />
                <span className="text-gray-400 text-[10px]">→</span>
                <RowChip row={r.target} tone="target" />
              </>
            )}
            {r.step === 'merge-approved' && (
              <>
                <RowChip row={r.source} />
                <span className="text-gray-400 text-[10px]">→</span>
                <RowChip row={r.target} tone="approved" />
              </>
            )}
            {(r.step === 'skip' || r.step === 'keep') && <RowChip row={r.row} />}
          </div>
          {!r.ok && (
            <div className="text-[11px] text-red-800 leading-snug">
              <span className="font-mono font-semibold">
                {r.httpStatus ? `HTTP ${r.httpStatus}` : 'Error'}:
              </span>{' '}
              {r.errorDetail}
              {r.hint && (
                <div className="text-red-700 italic mt-0.5">
                  Hint: {r.hint}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
};

const StepSection = ({ kind, results, filter }) => {
  const filtered =
    filter === 'failures-only' ? results.filter((r) => !r.ok) : results;
  if (results.length === 0 || filtered.length === 0) return null;
  const meta = stepMeta[kind];
  const Icon = meta.icon;
  const okCount = results.filter((r) => r.ok).length;
  const failedCount = results.length - okCount;
  return (
    <div className="border rounded overflow-hidden" data-testid={`report-step-${kind}`}>
      <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
        <Icon className="w-4 h-4 text-slate-700" />
        <span className="text-sm font-semibold text-slate-800">{meta.label}</span>
        <span className="text-[11px] text-emerald-700 ml-auto">
          {okCount}/{results.length} ok
        </span>
        {failedCount > 0 && (
          <span className="text-[11px] text-red-700 font-semibold">
            · {failedCount} failed
          </span>
        )}
      </div>
      <ul className="bg-white">
        {filtered.map((r, i) => (
          <ResultLine key={i} r={r} />
        ))}
      </ul>
    </div>
  );
};

const ApplyReportModal = ({ report, onClose, onRetry, retrying, onViewAudit }) => {
  const [filter, setFilter] = useState(
    report.totals.failed > 0 ? 'failures-only' : 'all',
  );

  // Group results by step, preserving execution order
  const grouped = useMemo(() => {
    const g = {};
    for (const k of STEP_ORDER) g[k] = [];
    for (const r of report.results) {
      if (g[r.step]) g[r.step].push(r);
    }
    return g;
  }, [report.results]);

  const retryableFailures = useMemo(
    () => report.results.filter((r) => !r.ok && r.retryable).length,
    [report.results],
  );

  const durationSec = (report.timing?.durationMs || 0) / 1000;
  const allOk = report.totals.failed === 0;

  const copyJson = () => {
    // Truncate question_text to keep the payload ticket-friendly
    const compact = {
      ...report,
      results: report.results.map((r) => ({
        ...r,
        source: r.source && { ...r.source, text: (r.source.text || '').slice(0, 80) },
        target: r.target && { ...r.target, text: (r.target.text || '').slice(0, 80) },
        row: r.row && { ...r.row, text: (r.row.text || '').slice(0, 80) },
      })),
    };
    try {
      navigator.clipboard.writeText(JSON.stringify(compact, null, 2));
      toast.success('Report copied to clipboard');
    } catch {
      toast.error('Copy failed — clipboard permission denied?');
    }
  };

  // "View in Audit Log" is delegated to a callback prop so the parent
  // (BulkSimilarityPage) can open BulkImportAuditLogModal with the
  // batchId pre-filtered — the audit log lives in a modal, not a route.
  // If no callback is wired, the button is disabled.

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="apply-report-modal"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`px-5 py-3 border-b ${
            allOk ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <h2
                className={`text-base font-semibold ${
                  allOk ? 'text-emerald-900' : 'text-amber-900'
                }`}
              >
                {allOk ? 'Apply complete' : 'Apply completed with errors'}
              </h2>
              <p
                className={`text-xs mt-0.5 flex items-center gap-2 ${
                  allOk ? 'text-emerald-700' : 'text-amber-800'
                }`}
              >
                <Clock className="w-3 h-3" />
                {durationSec.toFixed(1)}s ·{' '}
                <span className="font-semibold">{report.totals.ok}</span>{' '}
                succeeded
                {report.totals.failed > 0 && (
                  <>
                    ·{' '}
                    <span className="font-semibold text-red-700">
                      {report.totals.failed}
                    </span>{' '}
                    failed
                  </>
                )}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-900"
              aria-label="Close report"
              data-testid="apply-report-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Filter strip */}
        {report.totals.failed > 0 && (
          <div className="px-5 py-2 bg-white border-b flex items-center gap-3 text-[11px]">
            <span className="text-gray-600">Show:</span>
            <button
              onClick={() => setFilter('all')}
              className={`px-2 py-0.5 rounded border ${
                filter === 'all'
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-800 border-slate-300 hover:bg-slate-50'
              }`}
              data-testid="report-filter-all"
            >
              All ({report.totals.total})
            </button>
            <button
              onClick={() => setFilter('failures-only')}
              className={`px-2 py-0.5 rounded border ${
                filter === 'failures-only'
                  ? 'bg-red-700 text-white border-red-700'
                  : 'bg-white text-red-700 border-red-300 hover:bg-red-50'
              }`}
              data-testid="report-filter-failures"
            >
              Failures only ({report.totals.failed})
            </button>
          </div>
        )}

        {/* Body */}
        <div className="px-5 py-3 overflow-y-auto space-y-3">
          {STEP_ORDER.map((k) => (
            <StepSection
              key={k}
              kind={k}
              results={grouped[k]}
              filter={filter}
            />
          ))}
          {report.results.length === 0 && (
            <div className="text-center py-8 text-sm text-gray-500">
              No operations were executed.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t bg-gray-50 flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={copyJson}
            data-testid="report-copy-json-btn"
          >
            <ClipboardCopy className="w-4 h-4 mr-1" />
            Copy JSON
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewAudit && onViewAudit(report.batchId)}
            disabled={!report.batchId || !onViewAudit}
            data-testid="report-audit-link-btn"
          >
            <ExternalLink className="w-4 h-4 mr-1" />
            View in Audit Log
          </Button>
          <div className="ml-auto flex items-center gap-2">
            {retryableFailures > 0 && (
              <Button
                size="sm"
                onClick={onRetry}
                disabled={retrying}
                className="bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300"
                data-testid="report-retry-btn"
              >
                <RefreshCw
                  className={`w-4 h-4 mr-1 ${retrying ? 'animate-spin' : ''}`}
                />
                {retrying
                  ? 'Retrying…'
                  : `Retry failed (${retryableFailures})`}
              </Button>
            )}
            <Button
              size="sm"
              onClick={onClose}
              className="bg-slate-800 hover:bg-slate-900"
              data-testid="report-close-btn"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplyReportModal;
